import { CreateNotification, ManagementSecMainHeader, Table } from "components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { cancelScheduledNotification, getScheduledNotifications, updateScheduledNotification } from "apis/notification";
import { NOTIFICATION } from "common/routes";
import { formatDateToDDMMYYInNumber } from "shared/dates";

const formatScheduledFor = (scheduledAt) => {
    if (!scheduledAt) return "-";
    const parsed = new Date(scheduledAt);
    if (Number.isNaN(parsed.getTime())) return "-";

    const time = parsed.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
    });

    return `${formatDateToDDMMYYInNumber(parsed)} ${time}`;
};

const normaliseApiResponse = (apiResponse) => {
    if (!apiResponse || typeof apiResponse !== "object") return {};

    if ("success" in apiResponse || "message" in apiResponse || "pagination" in apiResponse) {
        return apiResponse;
    }

    if ("data" in apiResponse && typeof apiResponse.data === "object") {
        return apiResponse.data;
    }

    return apiResponse;
};

function Index() {
    const navigate = useNavigate();
    const [scheduledNotifications, setScheduledNotifications] = useState();
    const [pagination, setPagination] = useState({
        currentPage: 1,
        nextPage: null,
        prevPage: null,
    });
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState("queued");
    const [editingNotification, setEditingNotification] = useState(null);

    const canModifyNotification = useCallback(
        (status) => status === "queued",
        []
    );

    const fetchScheduled = useCallback(async (page = 1, limit = rowsPerPage) => {
        setScheduledNotifications(undefined);
        try {
            const response = await getScheduledNotifications({
                status: statusFilter,
                page,
                limit,
            });

            const payload = normaliseApiResponse(response) ?? {};
            const notifications = Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload)
                    ? payload
                    : [];
            const successFlag = typeof payload?.success === "boolean"
                ? payload.success
                : Array.isArray(notifications);
            const paginationInfo = payload?.pagination;
            const payloadMessage = payload?.message;

            if (successFlag) {
                setScheduledNotifications(notifications);
                setPagination({
                    currentPage: paginationInfo?.current_page ?? page,
                    nextPage: paginationInfo?.next_page ?? null,
                    prevPage: paginationInfo?.previous_page ?? null,
                });
            } else {
                setScheduledNotifications([]);
                setPagination({
                    currentPage: page,
                    nextPage: null,
                    prevPage: null,
                });
                toast.error(payloadMessage || "Failed to fetch scheduled notifications");
            }
        } catch (error) {
            setScheduledNotifications([]);
            setPagination({
                currentPage: page,
                nextPage: null,
                prevPage: null,
            });
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch scheduled notifications";
            toast.error(errorMessage);
        }
    }, [rowsPerPage, statusFilter]);

    const handleCancelScheduled = useCallback(async (notificationId) => {
        const confirmCancel = window.confirm("Are you sure you want to cancel this scheduled notification?");
        if (!confirmCancel) return;
        const toastId = toast.loading("Cancelling scheduled notification");
        try {
            const response = await cancelScheduledNotification(notificationId);
            toast.dismiss(toastId);

            const payload = normaliseApiResponse(response) ?? {};

            if (payload?.success !== false) {
                toast.success("Scheduled notification cancelled");
                fetchScheduled(pagination.currentPage || 1, rowsPerPage);
            } else {
                toast.error(payload?.message || "Failed to cancel scheduled notification");
            }
        } catch (error) {
            toast.dismiss(toastId);
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to cancel scheduled notification";
            toast.error(errorMessage);
        }
    }, [fetchScheduled, pagination.currentPage, rowsPerPage]);

    const columns = useMemo(() => [
        {
            Header: "No",
            accessor: (row, rowIndex) => rowIndex + 1,
            id: "no",
        },
        { Header: "Title", accessor: "title" },
        { Header: "Body", accessor: "body" },
        {
            Header: "Scheduled For",
            accessor: (row) => formatScheduledFor(row.scheduledAt),
            id: "scheduledFor",
        },
        {
            Header: "Status",
            accessor: (row) => row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : "-",
            id: "status",
        },
        { Header: "User Count", accessor: "user_count" },
        {
            Header: "Filter",
            accessor: "filters",
            Cell: ({ value }) => {
                if (!value || !Array.isArray(value)) return "-";
                return value.map((item, index) => (
                    <div key={index}>
                        <strong>Key:</strong> {item.key} <br />
                        <strong>Type:</strong> {item.filterType} <br />
                        <strong>Values:</strong> {item.values.join(", ")}
                    </div>
                ));
            },
        },
        {
            Header: "Actions",
            accessor: "actions",
            Cell: ({ row }) => {
                const notification = row.original;
                const isQueued = canModifyNotification(notification.status);
                return (
                    <div className="flex gap-2">
                        <button
                            className={`px-3 py-1 rounded ${isQueued ? "bg-green-500 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
                            onClick={() => isQueued && setEditingNotification(notification)}
                            disabled={!isQueued}
                        >
                            Edit
                        </button>
                        <button
                            className={`px-3 py-1 rounded ${isQueued ? "bg-red-500 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
                            onClick={() => isQueued && handleCancelScheduled(notification.id)}
                            disabled={!isQueued}
                        >
                            Cancel
                        </button>
                    </div>
                );
            },
        },
    ], [canModifyNotification, handleCancelScheduled]);

    useEffect(() => {
        fetchScheduled(1);
    }, [fetchScheduled]);

    const handleNavigation = () => {
        navigate(NOTIFICATION.NOTIFICATION);
    };

    const handleRowsChange = (value) => {
        const nextValue = Number(value);
        setRowsPerPage(nextValue);
    };

    const handleStatusChange = (value) => {
        setStatusFilter(value);
    };

    const handleUpdate = async (formData) => {
        if (!editingNotification) return;
        const toastId = toast.loading("Updating scheduled notification");
        const payload = {
            title: formData.title,
            body: formData.body,
        };

        if (formData.scheduled_date) {
            payload.scheduled_date = formData.scheduled_date;
        }

        if (formData.scheduled_time) {
            payload.scheduled_time = formData.scheduled_time;
        }

        try {
            const response = await updateScheduledNotification(editingNotification.id, payload);
            toast.dismiss(toastId);

            const payloadResponse = normaliseApiResponse(response) ?? {};

            if (payloadResponse?.success !== false) {
                toast.success("Scheduled notification updated");
                setEditingNotification(null);
                fetchScheduled(pagination.currentPage || 1, rowsPerPage);
            } else {
                toast.error(payloadResponse?.message || "Failed to update scheduled notification");
            }
        } catch (error) {
            toast.dismiss(toastId);
            const errorMessage = error?.response?.data?.message || error?.message || "Failed to update scheduled notification";
            toast.error(errorMessage);
        }
    };

    const handlePageChange = (nextPage) => {
        fetchScheduled(nextPage, rowsPerPage);
    };

    if (editingNotification) {
        return (
            <div className="flex flex-col h-screen bg-[#f8f8f8]">
                <div className="w-[96%] mx-auto my-10">
                    <ManagementSecMainHeader
                        mainHeading="Edit Scheduled Notification"
                        subHeading="Update the scheduled notification details"
                        btnText="Back to Scheduled"
                        handleBtn={() => setEditingNotification(null)}
                    />
                </div>
                <div className="w-[98%] flex items-center justify-center">
                    <CreateNotification
                        initialData={editingNotification}
                        handleCancel={() => setEditingNotification(null)}
                        handleSubmit={(value) => handleUpdate(value)}
                        allUser={false}
                        formTitle="Edit Scheduled Notification"
                        submitButtonLabel="Update"
                        cancel
                        disableScheduleToggle
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen">
            <div className="bg-[#f8f8f8] h-screen w-full">
                <div className="w-[96%] mx-auto my-10">
                    <ManagementSecMainHeader
                        mainHeading="Scheduled Notifications"
                        subHeading="Manage upcoming scheduled notifications"
                        btnText="Create Notification"
                        handleBtn={handleNavigation}
                    />
                </div>

                <div className="p-8">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center">
                            <label htmlFor="rowsPerPage" className="block text-sm font-medium text-gray-700 mr-2">
                                Rows per page:
                            </label>
                            <select
                                id="rowsPerPage"
                                name="rowsPerPage"
                                value={rowsPerPage}
                                onChange={(e) => handleRowsChange(e.target.value)}
                                className="block p-2 px-6 pl-3 pr-10 text-base border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            >
                                <option value={10}>10</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                                <option value={500}>500</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <label htmlFor="statusFilter" className="text-sm font-medium text-gray-700">
                                Status:
                            </label>
                            <select
                                id="statusFilter"
                                name="statusFilter"
                                value={statusFilter}
                                onChange={(e) => handleStatusChange(e.target.value)}
                                className="block p-2 px-4 text-base border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            >
                                <option value="queued">Queued</option>
                                <option value="sent">Sent</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>

                    </div>

                    {scheduledNotifications ? (
                        scheduledNotifications.length > 0 ? (
                            <Table
                                columns={columns}
                                data={scheduledNotifications}
                                pageNation={pagination}
                                handlePageNation={handlePageChange}
                                rowsPerPage={rowsPerPage}
                            />
                        ) : (
                            <div className="flex items-center justify-center w-full text-2xl text-black mt-10">
                                No scheduled notifications found
                            </div>
                        )
                    ) : (
                        <div className="flex items-center justify-center w-full text-2xl text-black mt-10">
                            Loading the Data Please Wait
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Index;
